import {showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {useState} from 'react';
import {useDispatch} from 'react-redux';

import {isEmpty, isNil, isNotNil} from 'ramda';

import {metadaApi} from '@dms/api/base';
import {documentContextApi} from '@dms/api/core';
import {
  GetCheckoutByIdApiResponse,
  metadaAftersalesCheckoutApi,
  Payment,
  PostCheckoutPaymentIssueApiResponse,
  usePatchCheckoutPaymentMutation,
  usePostCheckoutPaymentFullCorrectionIssueMutation,
  usePostCheckoutPaymentInvoiceIssueMutation,
  usePostCheckoutPaymentPartialCorrectionIssueMutation,
} from '@dms/api/sales';
import {
  DEFAULT_FOREIGN_CURRENCY,
  handleApiError,
  printPDF,
  useBank,
  useCheckoutIssuing,
  useFileNotification,
  useTenant,
} from '@dms/shared';

import {getApiDateString, precisionCalculation, useDebouncedCallback} from 'shared/web';

import {CheckoutPaymentFormType} from '../types/CheckoutPaymentFormType';
import {isCorrectiveTaxDocument} from '../utils/isCorrectiveTaxDocument';
import {useInvalidateServiceOrderJobs} from './useInvalidateServiceOrderJobs';

const SAVE_DEBOUNCE_MS = 1000;
const DOCUMENTS_COUNT_INVALIDATION_MS = 1000;
const DOCUMENT_GENERATION_TIMEOUT_MS = 30000;

interface UseCheckoutPaymentIssuingParams {
  checkoutId: string;
  paymentId: string;
  payment: Payment;
  checkout: GetCheckoutByIdApiResponse | undefined;
  serviceOrderId?: string;
}

export function useCheckoutPaymentIssuing(params: UseCheckoutPaymentIssuingParams) {
  const [isLazySaving, setIsLazySaving] = useState(false);
  const dispatch = useDispatch();

  const {getBankAccountByNumber} = useBank();
  const {tenantCurrency} = useTenant();
  const {setIsCheckoutIssuing} = useCheckoutIssuing();
  const invalidateServiceOrderJobs = useInvalidateServiceOrderJobs(params.serviceOrderId);
  const fileNotification = useFileNotification({
    timeoutMs: DOCUMENT_GENERATION_TIMEOUT_MS,
    onTimeout: () => showNotification.error(),
  });

  const paymentData = params.payment;
  const checkoutData = params.checkout;

  const [patchCheckoutPayment, {isLoading: isPatchCheckoutPaymentLoading}] =
    usePatchCheckoutPaymentMutation();
  const [postCheckoutPaymentInvoiceIssue, {isLoading: isPostCheckoutPaymentInvoiceIssueLoading}] =
    usePostCheckoutPaymentInvoiceIssueMutation();
  const [postPartialCorrectionIssue, {isLoading: isPostPartialCorrectionIssueLoading}] =
    usePostCheckoutPaymentPartialCorrectionIssueMutation();
  const [postFullCorrectionIssue, {isLoading: isPostFullCorrectionIssueLoading}] =
    usePostCheckoutPaymentFullCorrectionIssueMutation();

  const invalidateDocumentsCount = () => {
    setTimeout(() => {
      dispatch(documentContextApi.util.invalidateTags(['documentsCount']));
    }, DOCUMENTS_COUNT_INVALIDATION_MS);
  };

  const invalidateCheckout = () => {
    dispatch(
      metadaAftersalesCheckoutApi.util.invalidateTags([
        {type: 'afsCheckout', id: params.checkoutId},
      ])
    );
  };

  const invalidateServiceOrder = () => {
    const serviceOrderId = params.serviceOrderId;

    if (isNil(serviceOrderId) || isEmpty(serviceOrderId)) {
      return;
    }

    dispatch(metadaApi.util.invalidateTags([{type: 'serviceOrder', id: serviceOrderId}]));
  };

  const buildCorrectionDetails = (data: CheckoutPaymentFormType) => {
    if (isNil(data.dateOfReception) && isNil(data.correctionReason)) {
      return null;
    }

    return {
      dateOfReception: isNotNil(data.dateOfReception)
        ? getApiDateString(data.dateOfReception)
        : undefined,
      reason: data.correctionReason ?? undefined,
    };
  };

  const persistPaymentChanges = (data: CheckoutPaymentFormType) => {
    if (isNil(data.dateOfTaxableSupply) || isNil(data.dueDate) || isNil(data.issueDate)) {
      return undefined;
    }

    const bankAccount = isNotNil(data.bankAccount)
      ? getBankAccountByNumber(data.bankAccount)
      : undefined;

    const foreignCurrencyAmount =
      isNotNil(data.exchangeRate) && data.exchangeRate > 0
        ? parseFloat(precisionCalculation.divide(data.amount ?? 0, data.exchangeRate).toFixed(2))
        : 0;

    return patchCheckoutPayment({
      checkoutId: params.checkoutId,
      paymentId: params.paymentId,
      body: {
        paymentAmount: {
          amount: data.amount,
          currency: paymentData.paymentAmount?.currency ?? checkoutData?.currency ?? tenantCurrency,
        },
        foreignCurrencyPaymentAmount: checkoutData?.isForeignCurrencyPayment
          ? {
              amount: foreignCurrencyAmount,
              currency:
                paymentData.foreignCurrencyPaymentAmount?.currency ?? DEFAULT_FOREIGN_CURRENCY,
            }
          : undefined,
        dateOfTaxableSupply: getApiDateString(data.dateOfTaxableSupply),
        dueDate: getApiDateString(data.dueDate),
        issueDate: getApiDateString(data.issueDate),
        method: data.method,
        customerServiceContractId: data.customerServiceContractId,
        templateId: data.templateId,
        bankAccount: bankAccount
          ? {
              currency: bankAccount.currency ?? checkoutData?.currency ?? tenantCurrency,
              isPrimary: !!bankAccount.isPrimary,
              bankName: bankAccount.bankName,
              iban: bankAccount.iban,
              name: bankAccount.accountName,
              number: bankAccount.accountNumber,
              swift: bankAccount.swift,
            }
          : undefined,
        cashRegisterId: data.cashRegisterId,
        due: data.due,
        exchangeRate: data.exchangeRate ? String(data.exchangeRate) : undefined,
        note: data.note,
        correctionDetails: isCorrectiveTaxDocument(paymentData)
          ? buildCorrectionDetails(data)
          : undefined,
      },
    }).unwrap();
  };

  function handleSaveChanges(data: CheckoutPaymentFormType) {
    const savePromise = persistPaymentChanges(data);

    if (isNil(savePromise)) {
      setIsLazySaving(false);
      return;
    }

    return savePromise.catch(handleApiError).finally(() => setIsLazySaving(false));
  }

  const debouncedSaveChanges = useDebouncedCallback(handleSaveChanges, SAVE_DEBOUNCE_MS);

  const handleFormChange = (data: CheckoutPaymentFormType) => {
    setIsLazySaving(true);
    return debouncedSaveChanges(data);
  };

  const handleIssuedDocument = (res: PostCheckoutPaymentIssueApiResponse) => {
    const fileId = res?.file?.fileId;
    const pdfUrl = res?.file?.pdfUrl;

    if (isNil(fileId) || isNil(pdfUrl)) {
      return;
    }

    fileNotification.registerFile(fileId, {
      onSuccess: () => {
        invalidateCheckout();
        printPDF(pdfUrl);
      },
      onError: () => showNotification.error(),
    });
  };

  const issueDocument = () => {
    const issueArg = {checkoutId: params.checkoutId, paymentId: params.paymentId};

    return match(paymentData.discriminator)
      .with('BALANCE_CORRECTION_PARTIAL', () => postPartialCorrectionIssue(issueArg).unwrap())
      .with('BALANCE_CORRECTION_FULL', () => postFullCorrectionIssue(issueArg).unwrap())
      .otherwise(() => postCheckoutPaymentInvoiceIssue(issueArg).unwrap());
  };

  const issuePayment = async (data: CheckoutPaymentFormType) => {
    setIsCheckoutIssuing(true);

    const savePromise = persistPaymentChanges(data);

    if (isNotNil(savePromise)) {
      await savePromise
        .then(issueDocument)
        .then(handleIssuedDocument)
        .then(invalidateDocumentsCount)
        .then(invalidateServiceOrderJobs)
        .then(invalidateServiceOrder)
        .catch(handleApiError);
    }

    setIsLazySaving(false);
    setIsCheckoutIssuing(false);
  };

  const isIssuing =
    isPatchCheckoutPaymentLoading ||
    isPostCheckoutPaymentInvoiceIssueLoading ||
    isPostPartialCorrectionIssueLoading ||
    isPostFullCorrectionIssueLoading ||
    isLazySaving;

  return {handleFormChange, issuePayment, isLazySaving, isIssuing};
}
